import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  House, Buildings, Users as UsersIcon, Kanban, CheckSquare, ChartBar,
  Gear, SignOut, List, X, EnvelopeSimple, ShieldStar, MapTrifold, Target, Compass, ClipboardText, Bank, Scales,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";
import NotificationsBell from "@/components/NotificationsBell";
import { intakeText } from "@/i18n/propertyIntakeTranslations";
import { agentsText } from "@/i18n/aiAgentsTranslations";

const NAV = [
  { to: "/", label: "navigation.dashboard", icon: House, end: true },
  { to: "/properties", label: "navigation.properties", icon: Buildings },
  { to: "/property-intakes", label: "Angariação de imóveis", icon: ClipboardText },
  { to: "/places", label: "navigation.places", icon: MapTrifold },
  { to: "/contacts", label: "navigation.contacts", icon: UsersIcon },
  { to: "/international-buyers-dashboard", label: "buyerFinder.internalTitle", icon: UsersIcon },
  { to: "/investor-ownership", label: "investorOwnership.navigation", icon: UsersIcon },
  { to: "/requirements", label: "navigation.requirements", icon: Target },
  { to: "/opportunity-finder", label: "navigation.opportunityFinder", icon: Compass },
  { to: "/bank-opportunities", label: "bankOpportunities.navigation", icon: Bank },
  { to: "/construction-partners", label: "constructionPartners.navigation", icon: Buildings },
  { to: "/legal-partners", label: "legalPartners.navigation", icon: Scales },
  { to: "/agency-partners", label: "agencyPartners.navigation", icon: Buildings },
  { to: "/pipeline", label: "navigation.pipeline", icon: Kanban },
  { to: "/tasks", label: "navigation.tasks", icon: CheckSquare },
  { to: "/reports", label: "navigation.reports", icon: ChartBar },
  { to: "/settings", label: "navigation.settings", icon: Gear },
];

function NavItems({ items, onClick }) {
  const { t, i18n } = useTranslation();

  return (
    <nav className="flex-1 px-3 space-y-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onClick}
          data-testid={`nav-${item.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors rounded-sm ${
              isActive
                ? "bg-[#C5A059] text-white"
                : "text-slate-300 hover:bg-white/10 hover:text-white"
            }`
          }
        >
          <item.icon size={20} weight="regular" aria-hidden="true" />
          {item.to === "/ai-agents" ? agentsText(i18n.language).title : item.to === "/property-intakes" ? intakeText(i18n.language).navigation : t(item.label)}
        </NavLink>
      ))}
    </nav>
  );
}

import { feedText } from "@/i18n/propertyFeedText";

export default function Layout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = [...NAV];
  if (user?.role === "admin") {
    if (process.env.REACT_APP_PROPERTY_FEEDS_ENABLED === "true") {
      items.push({ to: "/property-integrations", label: feedText(i18n.language, "title"), icon: Buildings });
    }
    items.push({ to: "/messages", label: "navigation.messages", icon: EnvelopeSimple });
    items.push({ to: "/approvals", label: "navigation.approvals", icon: ClipboardText });
    items.push({ to: "/ai-worker", label: "workerNavigation", icon: ClipboardText });
    items.push({ to: "/ai-agents", label: "aiAgentsNavigation", icon: ClipboardText });
    items.push({ to: "/atlas", label: "ATLAS", icon: MapTrifold });
    items.push({ to: "/users", label: "navigation.users", icon: ShieldStar });
  }

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Sidebar = (
    <div className="flex flex-col h-full bg-[#0B2545] text-white">
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img
            src="/panatlantik-logo.png"
            alt={t('panatlantikInternational', 'PANATLANTIK INTERNATIONAL')}
            className="w-14 h-14 object-contain shrink-0"
          />
          <div className="min-w-0">
            <div className="font-serif text-lg leading-tight tracking-tight">
              {t('panatlantikInternational', 'PANATLANTIK INTERNATIONAL')}
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mt-1">
              {t('realEstateCrm', 'Real Estate CRM')}
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        <NavItems items={items} onClick={() => setOpen(false)} />
      </div>
      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-9 h-9 rounded-sm bg-[#C5A059] flex items-center justify-center text-sm font-semibold">
            {(user?.name || "U").charAt(0)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-slate-400 capitalize">{user?.role}</div>
          </div>
        </div>
        <LanguageSelector />
        <NotificationsBell />
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-colors rounded-sm"
        >
          <SignOut size={18} aria-hidden="true" /> {t("navigation.signOut")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      <aside className="hidden lg:flex w-64 flex-shrink-0 fixed inset-y-0">{Sidebar}</aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="w-64 h-full">{Sidebar}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-slate-200 flex items-center justify-between px-4 h-14">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label={t("menu", "Menu")}>
            {open ? <X size={24} /> : <List size={24} />}
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <img
              src="/panatlantik-logo.png"
              alt={t('panatlantikInternational', 'PANATLANTIK INTERNATIONAL')}
              className="w-8 h-8 object-contain shrink-0"
            />
            <span className="font-serif text-sm sm:text-base leading-tight truncate">
              {t('panatlantikInternational', 'PANATLANTIK INTERNATIONAL')}
            </span>
          </div>
          <div className="w-8 h-8 rounded-sm bg-[#0B2545] text-white flex items-center justify-center text-xs">
            {(user?.name || "U").charAt(0)}
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
